import { AuthService } from 'src/app/services/app/auth.service';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { isPlatform } from '@ionic/angular';
import { CookieService } from 'ngx-cookie-service';
import { SubscriberLoginResp } from 'src/app/models/common/SubscriberLoginResp';
import { RuntimeConfigService } from 'src/app/services/app/runtime-config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  constructor(private authService: AuthService, private router: Router, private cookieService: CookieService, private runtimeConfigService: RuntimeConfigService) { }

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    await this.authService.restorePersistedSubscriberSessionAsync();

    let isLoggedIn = false;
    let userType: 'subscriber' | 'portalUser' | 'companyUser' | 'blogUser' | null = null;
    let subscriberId = '';
    let hasValidCompanyToken = false;

    const isElectron = this.runtimeConfigService.isElectron;

    if (isPlatform('capacitor')) {
      const companyUserString = sessionStorage.getItem('companyUser');
      const companyToken = sessionStorage.getItem('ctoken');
      hasValidCompanyToken = !!companyToken && companyToken !== 'undefined' && companyToken !== 'null';
      if (companyUserString || hasValidCompanyToken) {
        isLoggedIn = true;
        userType = 'companyUser';
      }

      const subscriber = await this.authService.getPreferenceSubscriberDetails();
      const portalUserString = sessionStorage.getItem('portalUser');
      if (!isLoggedIn && (subscriber || portalUserString)) {
        isLoggedIn = true;
        userType = portalUserString ? 'portalUser' : 'subscriber';
        subscriberId = subscriber?.subscriberID?.toString() || sessionStorage.getItem('subscriberID') || '';
      }

      const blogUserString = sessionStorage.getItem('blogUser');
      if (!isLoggedIn && blogUserString) {
        isLoggedIn = true;
        userType = 'blogUser';
      }
    } else if(isElectron) {
      const companyUser = localStorage.getItem('companyUser') || sessionStorage.getItem('companyUser');
      const companyToken = localStorage.getItem('ctoken') || sessionStorage.getItem('ctoken');
      hasValidCompanyToken = !!companyToken && companyToken !== 'undefined' && companyToken !== 'null';
      if (companyUser || hasValidCompanyToken) {
        isLoggedIn = true;
        userType = 'companyUser';
      }

      const subscriber = localStorage.getItem('subscriber') || sessionStorage.getItem('subscriber');
      const portalUser = localStorage.getItem('portalUser') || sessionStorage.getItem('portalUser');
      if(!isLoggedIn && (subscriber || portalUser)) {
        isLoggedIn = true;
        userType = portalUser ? 'portalUser' : 'subscriber';
        subscriberId = localStorage.getItem('subscriberID') || sessionStorage.getItem('subscriberID');
      }

      const blogUser = localStorage.getItem('blogUser') || sessionStorage.getItem('blogUser');
      if (!isLoggedIn && blogUser) {
        isLoggedIn = true;
        userType = 'blogUser';
      }
    } else {
      const subscriberString = sessionStorage.getItem('subscriber') || sessionStorage.getItem('portalUser') || this.cookieService.get('subscriber');
      const blogUserString = sessionStorage.getItem('blogUser');
      const companyUserString = sessionStorage.getItem('companyUser');
      const companyToken = sessionStorage.getItem('ctoken');
      hasValidCompanyToken = !!companyToken && companyToken !== 'undefined' && companyToken !== 'null';

      if (companyUserString || hasValidCompanyToken) {
        isLoggedIn = true;
        userType = 'companyUser';
      } else if (subscriberString) {
        isLoggedIn = true;
        userType = sessionStorage.getItem('portalUser') ? 'portalUser' : 'subscriber';
        subscriberId = sessionStorage.getItem('subscriberID') || this.cookieService.get('subscriberID');
      } else if (blogUserString) {
        isLoggedIn = true;
        userType = 'blogUser';
      }
    }

    // Handle root route (landing page)
    if (state.url === '/') {
      if (isLoggedIn) {
        if (userType === 'companyUser' || hasValidCompanyToken) {
          await this.router.navigate(['admin', 'home']);
          return false;
        } else if (userType === 'subscriber' || userType === 'portalUser') {
          await this.router.navigate(['view-company'], { queryParams: { subscriberID: subscriberId } });
          return false; // Prevent further route activation
        } else if (userType === 'blogUser') {
          await this.router.navigate(['view-blog']);
          return false;
        }
      }
      return true; // Allow access to the landing page
    }

    // If already authenticated, keep users out of auth screens and route by priority.
    const isAuthScreen =
      state.url.startsWith('/login') ||
      state.url.startsWith('/subscriber-login') ||
      state.url.startsWith('/blog-login');

    if (isAuthScreen && isLoggedIn) {
      if (userType === 'companyUser' || hasValidCompanyToken) {
        await this.router.navigate(['admin', 'home']);
      } else if (userType === 'subscriber' || userType === 'portalUser') {
        await this.router.navigate(['view-company'], { queryParams: { subscriberID: subscriberId } });
      } else if (userType === 'blogUser') {
        await this.router.navigate(['view-blog']);
      }
      return false;
    }

    // Admin routes are company-context only.
    if (state.url.startsWith('/admin')) {
      if (userType === 'companyUser' || hasValidCompanyToken) {
        return true;
      }

      if (userType === 'subscriber') {
        await this.router.navigate(['view-company'], { queryParams: { subscriberID: subscriberId } });
      } else if (userType === 'blogUser') {
        await this.router.navigate(['view-blog']);
      } else {
        await this.router.navigate(['subscriber-login']);
      }
      return false;
    }

    // Handle other routes (protected)
    if (isLoggedIn) {
      return true; // Allow access to protected routes
    } else {
      await this.router.navigate(['subscriber-login']);
      return false; // Redirect to login for unauthenticated users
    }
  }
}
